import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Menu, MenuItem, Checkbox, Button } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { StructuredWorkoutIcon } from '../icons';
import { CalendarEventDto } from '../../types/calendar';
import { AddEventButtons } from '../common/AddEventButtons';
import styles from './WorkoutsWidget.module.css';
import { WorkoutSummary } from '../../types/dashboard.types';

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '16px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

const EventLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
}));

const EventContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
  cursor: 'pointer',
}));

interface WorkoutItemProps {
  event: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
  onView: (event: CalendarEventDto) => void;
}

const WorkoutItem: React.FC<WorkoutItemProps> = ({ event, onEdit, onDelete, onToggleComplete, onView }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = event.actions.includes('edit');
  const canDelete = event.actions.includes('delete');
  const canComplete = event.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(event);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(event.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(event.id, e.target.checked);
  };

  const formattedTime = new Date(event.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  const displayText = event.title;
  const calories = (event.details as any)?.calc?.cal || (event as any).calories;

  return (
    <EventLayout>
      <Checkbox checked={event.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <EventContent onClick={() => onView(event)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
            {displayText}
          </Typography>
          {event.isStructured && (
            <StructuredWorkoutIcon fontSize="small" sx={{ color: '#FF7F7F' }} />
          )}
          {calories && (
            <Typography variant="body2" sx={{ color: '#FF7F7F', fontWeight: 600, fontSize: '13px', lineHeight: '24px' }}>
              {calories}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
      </EventContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </EventLayout>
  );
};

interface WorkoutsWidgetProps {
  workouts: CalendarEventDto[];
  workoutSummary: WorkoutSummary | null;
  onAddWorkout?: () => void;
  onQuickAddWorkout?: () => void;
  onEditWorkout?: (event: CalendarEventDto) => void;
  onDeleteWorkout?: (eventId: number) => void;
  onToggleWorkoutComplete?: (eventId: number, isCompleted: boolean) => void;
  onViewWorkout?: (event: CalendarEventDto) => void;
}

export const WorkoutsWidget: React.FC<WorkoutsWidgetProps> = ({ 
  workouts, 
  workoutSummary, 
  onAddWorkout, 
  onQuickAddWorkout,
  onEditWorkout, 
  onDeleteWorkout, 
  onToggleWorkoutComplete, 
  onViewWorkout 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEdit = (event: CalendarEventDto) => {
    if (onEditWorkout) onEditWorkout(event);
  };

  const handleDelete = (eventId: number) => {
    if (onDeleteWorkout) onDeleteWorkout(eventId);
  };

  const handleToggleComplete = (eventId: number, isCompleted: boolean) => {
    if (onToggleWorkoutComplete) {
      onToggleWorkoutComplete(eventId, isCompleted);
    }
  };

  const handleView = (event: CalendarEventDto) => {
    if (onViewWorkout) onViewWorkout(event);
  }

  const getProgressBarColor = (percentage: number) => {
    let hue;
    const saturation = 100;
    const lightness = 50;

    if (percentage <= 100) {
      hue = 220 - (percentage / 100) * 100;
    } else {
      const normalizedOverPercentage = Math.min((percentage - 100) / 25, 1);
      hue = 120 - normalizedOverPercentage * 120;
    }

    hue = Math.max(0, Math.min(360, hue));
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const safeSummary = workoutSummary || { percentage: 0, actual: 0, goal: 0, unit: '' };
  const fillPercentage = Math.min(safeSummary.percentage, 100);
  const progressBarColor = getProgressBarColor(safeSummary.percentage);

  const displayedWorkouts = isExpanded ? workouts : workouts.slice(0, 3);
  const remainingWorkoutsCount = workouts.length - displayedWorkouts.length;

  return (
    <StyledPaper>
      <div className={styles.titleLayout}>
        <div className={styles.iconWrapper}>
          <FitnessCenterIcon sx={{ color: '#41AEE4' }} />
        </div>
        <span className={styles.titleText}>Активність</span>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div className={styles.progressBackground} />
          <div
            className={styles.progressFill}
            style={{
              width: `${fillPercentage}%`,
              backgroundColor: progressBarColor,
            }}
          />
        </div>
        <div className={styles.statsRow}>
          <span className={styles.percentage}>{safeSummary.percentage}%</span>
          <div className={styles.volumeInfo}>
            <span className={styles.actual}>{safeSummary.actual}</span>
            <span className={styles.goal}>/{safeSummary.goal} {safeSummary.unit}</span>
          </div>
        </div>
      </div>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
        {displayedWorkouts.length > 0 ? (
          displayedWorkouts.map((workout) => (
            <WorkoutItem 
              key={workout.id} 
              event={workout} 
              onEdit={handleEdit} 
              onDelete={handleDelete} 
              onToggleComplete={handleToggleComplete}
              onView={handleView}
            />
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
            Поки що немає доданих тренувань.
          </Typography>
        )}
        {workouts.length > 3 && (
          <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
            {isExpanded ? 'Показати менше' : `Показати ще ${remainingWorkoutsCount}...`}
          </Button>
        )}
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 'auto', gap: '8px' }}>
        <AddEventButtons
          onAddText={onAddWorkout}
          showTextButton={true}
          showVoiceButton={false}
          showPhotoButton={false}
          buttonColor="#FF7F7F"
        />
        <IconButton 
          onClick={onQuickAddWorkout}
          sx={{ 
            backgroundColor: '#F2F2F2', 
            borderRadius: '12px', 
            width: '91.67px', 
            height: '56px',
            color: '#FF9800'
          }}
        >
          <AutoFixHighIcon fontSize="small" />
        </IconButton>
      </Box>
    </StyledPaper>
  );
};
